import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePurchaseOrderDetails } from "../../hooks/usePurchaseOrderDetails";
import { useMe } from "../../../../swr/useMe";
import { useMePartner } from "../../../../swr/useMePartner";
import { Role } from "../../../../api";
import { PageContentSkeleton } from "../../../../components/common/page-content-skeleton";
import { PageContainer } from "../../../../components/common/page-container";
import { Breadcrumbs } from "../../../../components/common/breadcrumbs";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { PurchaseOrderHeader } from "../components/purchase-order-header";
import { PurchaseOrderAddress } from "../components/purchase-order-address";
import { PurchaseOrderProductsAndSummary } from "../components/purchase-order-products-and-summary";
import { PurchaseOrderNotFound } from "../components/purchase-order-not-found";
import { MarkInDeliveryDialog } from "../components/mark-in-delivery-dialog";
import { OrderShipmentsCard } from "../../../../components/common/order-shipments-card";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { Heading, Text } from "../../../../components/common/typography";
import { purchaseOrdersService } from "../../../api_client";
import { showSuccessToast, showErrorToast } from "../../../../utils/toasts";
import { mutate } from "swr";
import { IconArrowRight, IconCircleCheck, IconCurrencyDollar, IconPrinter, IconTruckDelivery } from "@tabler/icons-react";
import { getPurchaseOrderCurrency } from "../../utils/purchase-order-currency";
import { InternalDeliveryCostEditor } from "../components/internal-delivery-cost-editor";
import { PurchaseOrderInfoCard } from "../components/purchase-order-info-card";
import { SellerOrderInvoicesCard } from "../components/seller-order-invoices-card";
import { PayVendorBillDialog } from "../../../payments/presentation/components/pay-vendor-bill-dialog";
import type { VendorBillDto } from "../../../payments/data/types";
import { VendorBillPaymentState } from "../../../payments/data/types";

const PO_DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
function formatPoDate(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : PO_DATE_FMT.format(d);
}

function getPoStatusChip(state?: string | null, isInDelivery = false, isDelivered = false) {
  if (state === "cancel") return <Chip variant="rejected">Cancelled</Chip>;
  if (isDelivered) return <Chip variant="approved">Delivered</Chip>;
  if (isInDelivery) return <Chip variant="underReview">In Transit</Chip>;
  if (state === "purchase") return <Chip variant="tertiary">Confirmed</Chip>;
  if (state === "done") return <Chip variant="approved">Done</Chip>;
  return <Chip variant="primary">Draft</Chip>;
}

export function PurchaseOrderDetailsPage() {
    const { id } = useParams<{
        id: string;
    }>();
    const navigate = useNavigate();
    const purchaseOrderId = Number(id ?? 0);
    const { data: purchaseOrder, error, isLoading, mutate: mutatePurchaseOrder, } = usePurchaseOrderDetails(purchaseOrderId);
    const { user } = useMe();
    const { isSeller } = useMePartner();
    const location = useLocation();
    const isAdminContext = location.pathname.startsWith("/admin");
    const fromLabel = (location.state as { from?: string } | null)?.from;
    const [isMarkInDeliveryDialogOpen, setIsMarkInDeliveryDialogOpen] = useState(false);
    const [payBillOpen, setPayBillOpen] = useState(false);
    if (isLoading) {
        return (<PageContainer>
        <PageContentSkeleton withSideCard cardCount={2} actionCount={2} />
      </PageContainer>);
    }
    if (error || !purchaseOrder) {
        return (<PageContainer>
        <PurchaseOrderNotFound />
      </PageContainer>);
    }
    // Calculate tax percentage from amountTax and amountUntaxed
    const taxPercentage = purchaseOrder.amountUntaxed > 0
        ? Math.round((purchaseOrder.amountTax / purchaseOrder.amountUntaxed) * 100)
        : 0;
    const currency = getPurchaseOrderCurrency(purchaseOrder.partner ?? purchaseOrder.destAddress);
    // Check delivery status using deliveredAt field
    const isDelivered = !!purchaseOrder.deliveredAt;
    // Check dropshipping states for "In Delivery" status
    const hasDropshipping = purchaseOrder.dropshipping && purchaseOrder.dropshipping.length > 0;
    const isInDelivery = hasDropshipping && !isDelivered;
    // Check if state is "draft" for showing "Mark In Delivery" button
    const state = purchaseOrder.state?.toLowerCase() || "";
    const isDraft = state === "draft";
    // Only show "Mark Delivered" button when in delivery and not already delivered
    const showMarkDeliveredButton = isInDelivery && !isDelivered;
    const handleMarkInDelivery = async (datePlanned: string, deliveryUrl?: string) => {
        try {
            await purchaseOrdersService.confirmPurchaseOrder(purchaseOrderId, {
                date_planned: datePlanned,
                deliveryUrl: deliveryUrl?.trim() || undefined,
            });
            showSuccessToast("Purchase order marked in delivery successfully");
            // Invalidate purchase order cache to refresh data
            await mutate(`purchase-order-details-${purchaseOrderId}`);
            mutatePurchaseOrder?.();
        }
        catch (error) {
            const errorMessage = error instanceof Error
                ? error.message
                : "Failed to mark order in delivery. Please try again.";
            showErrorToast(errorMessage);
            throw error;
        }
    };
    // Only sellers (not admins) can mark orders in delivery
    const isAdmin = user?.role === Role.Admin;
    const canMarkInDelivery = isDraft && !isDelivered && !isInDelivery && isSeller && !isAdmin;
    const renderActions = () => (<div className="flex flex-wrap gap-3">
      {/* View Purchase Order button - always visible */}
      {!canMarkInDelivery && (<button className="px-4 py-3 border border-uiSecondary rounded-lg text-primary hover:bg-uiSecondary transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => {
                if (purchaseOrder.previewUrl) {
                    window.open(purchaseOrder.previewUrl, "_blank", "noopener,noreferrer");
                }
            }} disabled={!purchaseOrder.previewUrl}>
          <IconPrinter size={20}/>
          <Text variant="body" tone="dark">
            View Purchase Order
          </Text>
        </button>)}
      {/* Mark In Delivery button - only for draft state */}
      {canMarkInDelivery && (<Button variant="primary" size="md" onClick={() => setIsMarkInDeliveryDialogOpen(true)}>
          <IconTruckDelivery size={20} style={{
                marginRight: 1
            }}/>
          Mark In Delivery
        </Button>)}
      {showMarkDeliveredButton && (<Button variant="primary" size="md" onClick={() => navigate(AppRoutes.CONFIRM_DELIVERY.replace(":id", purchaseOrderId.toString()))}>
          <IconCircleCheck size={20} style={{
                marginRight: 1
            }}/>
          Mark Delivered
        </Button>)}
    </div>);
    return (<PageContainer>
      <div className="flex flex-col gap-6 w-full">
        {/* Breadcrumbs */}
        {fromLabel ? (
          <div className="flex items-center gap-2">
            <button onClick={() => location.key !== "default" ? navigate(-1) : navigate(isAdminContext ? AppRoutes.ADMIN_PURCHASE_ORDERS : AppRoutes.PURCHASE_ORDERS)} className="hover:underline">
              <Text variant="bodyBold" tone="accent">← {fromLabel}</Text>
            </button>
            <Text variant="body" tone="muted">/</Text>
            <Text variant="body" tone="muted">{purchaseOrder.name}</Text>
          </div>
        ) : (
          <Breadcrumbs items={[
              { label: "Home", path: AppRoutes.INDEX },
              { label: "Purchase Orders", path: isAdminContext ? AppRoutes.ADMIN_PURCHASE_ORDERS : AppRoutes.PURCHASE_ORDERS },
              { label: purchaseOrder.name },
          ]}/>
        )}

        {/* Linked Sale Order */}
        {isAdminContext && (purchaseOrder as any).linkedSaleOrder && (
          <button
            type="button"
            onClick={() => navigate(AppRoutes.ADMIN_ORDER_DETAILS.replace(":id", String((purchaseOrder as any).linkedSaleOrder.id)), { state: { from: `Purchase Order ${purchaseOrder.name}` } })}
            className="flex w-full items-center justify-between rounded-xl border border-borderDefault bg-bgWhite px-5 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgLight max-md:px-4"
          >
            <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-0">
              <Text variant="bodySmMedium" tone="muted">Linked Sale Order:</Text>
              <Text variant="bodySmBold" tone="dark">{(purchaseOrder as any).linkedSaleOrder.name}</Text>
            </div>
            <IconArrowRight size={16} className="text-textMuted" />
          </button>
        )}

        {/* Purchase Order Title */}
        {isAdminContext ? (
          <div className="flex items-start justify-between gap-4 max-md:flex-col max-md:gap-2">
            <div className="flex items-center gap-3 max-md:flex-wrap">
              <Heading variant="h2" tone="dark">{purchaseOrder.name}</Heading>
              {getPoStatusChip(purchaseOrder.state, isInDelivery, isDelivered)}
            </div>
            <Text variant="bodySm" tone="muted">
              {purchaseOrder.partner?.name ?? ""}{purchaseOrder.dateOrder ? ` · ${formatPoDate(purchaseOrder.dateOrder)}` : ""}
            </Text>
          </div>
        ) : (
          <PurchaseOrderHeader purchaseOrder={purchaseOrder} isInDelivery={isInDelivery} isDelivered={isDelivered} deliveredAt={purchaseOrder.deliveredAt} actions={renderActions()}/>
        )}

        {isAdminContext && isDelivered && (() => {
          const vb = (purchaseOrder as unknown as {
            vendorBill?: { id: number; name: string; previewUrl: string | null; paymentState: string | null; amountResidual: number | null; amountTotal: number | null } | null;
          }).vendorBill;
          if (!vb) return null;
          const isPaid = vb.paymentState === "paid";
          return (
            <div className={`flex items-center justify-between rounded-2xl border px-6 py-4 ${isPaid ? "border-borderDefault bg-bgWhite" : "border-accentAmber/30 bg-accentAmberTintBg"}`}>
              <div>
                <Text variant="bodySmBold" tone="dark">Seller Payment</Text>
                <Text variant="bodyXs" tone="muted">
                  {isPaid
                    ? `Vendor bill ${vb.name} has been paid.`
                    : `Outstanding: ${vb.amountResidual ?? vb.amountTotal ?? 0} ${currency} — ${vb.name}`}
                </Text>
              </div>
              {isPaid ? (
                <Chip variant="approved">Paid</Chip>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={<IconCurrencyDollar size={18} />}
                  onClick={() => setPayBillOpen(true)}
                >
                  Confirm Payment
                </Button>
              )}
            </div>
          );
        })()}

        {/* Partner Information */}
        <PurchaseOrderAddress partner={purchaseOrder.partner} destAddress={purchaseOrder.destAddress}/>

        <PurchaseOrderInfoCard
          order={purchaseOrder}
          isAdmin={isAdminContext}
          buyerPartner={
            (purchaseOrder as any).linkedSaleOrder?.partnerId
              ? { id: (purchaseOrder as any).linkedSaleOrder.partnerId, name: (purchaseOrder as any).linkedSaleOrder.partnerName ?? "Buyer" }
              : null
          }
        />

        <OrderShipmentsCard
          shipments={purchaseOrder.dropshipping}
          title="Delivery Information"
          showInternalCosts={isAdmin}
        />

        {isDelivered && (
          <SellerOrderInvoicesCard
            swrKey={`purchase-order-details-${purchaseOrderId}`}
            purchaseOrderId={purchaseOrderId}
            status={isDelivered ? "delivered" : "processing"}
            sellerInvoice={
              (purchaseOrder as unknown as {
                sellerInvoice?: { id: number; name: string; mimetype: string; url: string } | null;
              }).sellerInvoice
            }
            vendorBill={
              (purchaseOrder as unknown as {
                vendorBill?: { id: number; name: string; previewUrl: string | null } | null;
              }).vendorBill
            }
            canEdit={!isAdminContext}
          />
        )}

        {isAdmin && (
          <InternalDeliveryCostEditor
            purchaseOrderId={purchaseOrderId}
            shipments={purchaseOrder.dropshipping}
            onSaved={async () => {
              await mutate(`purchase-order-details-${purchaseOrderId}`);
              mutatePurchaseOrder?.();
            }}
          />
        )}

        {/* Products Table and Amount Summary */}
        <PurchaseOrderProductsAndSummary orderLines={purchaseOrder.orderLines} taxPercentage={taxPercentage} amountUntaxed={purchaseOrder.amountUntaxed} amountTax={purchaseOrder.amountTax} amountTotal={purchaseOrder.amountTotal} currency={currency}/>
      </div>

      {/* Mark In Delivery Dialog */}
      <MarkInDeliveryDialog open={isMarkInDeliveryDialogOpen} setOpen={setIsMarkInDeliveryDialogOpen} onConfirm={handleMarkInDelivery}/>

      {/* Pay Vendor Bill Dialog (admin only) */}
      {isAdminContext && (() => {
        const vb = (purchaseOrder as unknown as {
          vendorBill?: { id: number; name: string; previewUrl: string | null; paymentState: string | null; amountResidual: number | null; amountTotal: number | null } | null;
        }).vendorBill;
        if (!vb) return null;
        const billDto: VendorBillDto = {
          id: vb.id,
          supplierId: purchaseOrder.partner?.id ?? null,
          supplierName: purchaseOrder.partner?.name ?? null,
          reference: vb.name,
          invoiceDate: null,
          dueDate: null,
          amountTotal: vb.amountTotal ?? purchaseOrder.amountTotal ?? 0,
          amountResidual: vb.amountResidual ?? vb.amountTotal ?? 0,
          currency: currency,
          paymentState: (vb.paymentState === "paid" ? VendorBillPaymentState.PAID : VendorBillPaymentState.NOT_PAID),
          state: "posted",
        };
        return (
          <PayVendorBillDialog
            bill={billDto}
            open={payBillOpen}
            onClose={() => setPayBillOpen(false)}
            onSuccess={async () => {
              showSuccessToast("Payment confirmed successfully.");
              await mutate(`purchase-order-details-${purchaseOrderId}`);
              mutatePurchaseOrder?.();
            }}
          />
        );
      })()}
    </PageContainer>);
}
